import { api } from "../lib/api";

export const getPreferences = () => api("/viewer/preferences");
export const putPreferences = async (body) => {
  const r = await api("/viewer/preferences", { method: "PUT", body });
  if (r.ok && _viewer) _viewer.prefs = { ..._viewer.prefs, ...r.data }; // the write IS the invalidation
  return r;
};
export const getViewerCountries = () => api("/viewer/countries");
export const getContinents = () => api("/viewer/continents");

// ONE authoritative load of the signed-in account's viewer data (preferences + geography lists)
// for the life of the page. Every consumer (WorldExperience, LinkApproval) asks here instead of
// fetching on mount, so a remount never re-fetches. Keyed by www_user_id: a different account
// always fetches fresh; forgetViewerData() runs on sign-out. Not a time-based cache — the only
// writer is putPreferences above, which updates the slot from the server's confirmed response.
let _viewer = null; // { uid, prefs, countries, continents }

export async function loadViewerData(uid) {
  if (_viewer && _viewer.uid === uid) return { ..._viewer, fromCache: true };
  const [p, c, ct] = await Promise.all([getPreferences(), getViewerCountries(), getContinents()]);
  const data = {
    uid,
    prefs: p.ok ? p.data : null,
    countries: c.ok ? (c.data.countries || []) : [],
    continents: ct.ok ? (ct.data.continents || []) : [],
    status: p.status,
  };
  if (p.ok && c.ok && ct.ok) _viewer = data; // only a complete, successful load is reusable
  return { ...data, fromCache: false };
}

export function forgetViewerData() { _viewer = null; }
export function _viewerSlotForTests() { return _viewer; }
export const getRandom = (variant = "desktop", photoId = null) =>
  api(`/viewer/random?variant=${variant}${photoId ? `&photo_id=${photoId}` : ""}`);
export const getEligibleIds = () => api("/viewer/eligible-ids");
export const reportPhoto = (photo_id) =>
  api("/moderation/report", { method: "POST", body: { photo_id, reason_code: "other", detail: "reported from viewer" } });

export const SLIDESHOW_OPTIONS = [5, 10, 20, 30, 60];

export function nameFor(list, code) {
  const hit = (list || []).find((x) => x.code === code);
  return hit ? hit.name : code;
}

export function scopeLabel(prefs, countries, continents) {
  const geoParts = [];
  (prefs.continent_codes || []).forEach((c) => geoParts.push(nameFor(continents, c)));
  (prefs.country_codes || []).forEach((c) => geoParts.push(shortCountry(nameFor(countries, c))));
  const geo = geoParts.length ? geoParts.join(" · ") : "The whole world";
  const f = prefs.date_from_year, t = prefs.date_to_year;
  let when = "Any time";
  if (f && t) when = f === t ? `${f}` : `${f}–${t}`;
  else if (f) when = `From ${f}`;
  else if (t) when = `Until ${t}`;
  return { geo, when, full: `${geo} · ${when}` };
}

// One vocabulary everywhere: names are displayed exactly as the backend serves them (ISO short
// names). The old client-side trimming drifted from Android and even collapsed distinct entries
// ("Congo, Democratic Republic of the" -> "Congo"), so it is now a no-op kept for call sites.
export function shortCountry(name) {
  return name;
}

// Search rule shared verbatim with the backend (geography.normalise) and Android (CountrySearch):
// lowercase, fold diacritics, "st " -> "saint ". Common names come from the server's `aliases`.
export function normaliseCountryQuery(text) {
  const folded = (text || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  let s = folded.toLowerCase().replace(/\./g, " ").split(/\s+/).filter(Boolean).join(" ");
  if (s.startsWith("st ")) s = "saint " + s.slice(3);
  return s;
}

export function countryMatches(country, query) {
  const q = normaliseCountryQuery(query);
  if (!q) return true;
  if (normaliseCountryQuery(country.code) === q) return true;
  if (normaliseCountryQuery(country.name).includes(q)) return true;
  return (country.aliases || []).some((a) => normaliseCountryQuery(a).includes(q));
}
